/**
 * One verdict sheet for the whole app, opened from anywhere.
 *
 * Four separate cards render a CookedStamp, and two more screens capture a
 * cook. Giving each of them its own copy of the sheet plus its own visibility
 * state is how the three rating entry points drifted apart in the first place
 * (#512). Instead the sheet is mounted once, above the navigator, and the
 * gestures call `openVerdict(...)`.
 *
 * Deliberately not Redux: this is ephemeral view state that nothing needs to
 * persist, sync, or replay.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { HouseholdVerdictSheet } from './HouseholdVerdictSheet';
import type { VerdictSurface } from './VerdictRows';

interface VerdictRequest {
  recipeId: string;
  recipeTitle: string;
  surface: VerdictSurface;
}

interface VerdictPromptApi {
  openVerdict: (request: VerdictRequest) => void;
  closeVerdict: () => void;
}

const noop: VerdictPromptApi = { openVerdict: () => {}, closeVerdict: () => {} };

const VerdictPromptContext = createContext<VerdictPromptApi>(noop);

/**
 * Ask the household what it thought of a recipe.
 *
 * Safe to call from a tree with no provider (tests, storybook-ish renders):
 * the default is a no-op rather than a throw, so a card never crashes a screen
 * because the prompt is not mounted.
 */
export const useVerdictPrompt = (): VerdictPromptApi => useContext(VerdictPromptContext);

export function VerdictPromptProvider({ children }: { children: React.ReactNode }) {
  const [request, setRequest] = useState<VerdictRequest | null>(null);
  const router = useRouter();

  const openVerdict = useCallback((next: VerdictRequest) => setRequest(next), []);
  const closeVerdict = useCallback(() => setRequest(null), []);

  const api = useMemo(() => ({ openVerdict, closeVerdict }), [openVerdict, closeVerdict]);

  const handleAddPeople = useCallback(() => {
    setRequest(null);
    router.push('/share-partner');
  }, [router]);

  return (
    <VerdictPromptContext.Provider value={api}>
      {children}
      {request && (
        <HouseholdVerdictSheet
          visible
          recipeId={request.recipeId}
          recipeTitle={request.recipeTitle}
          surface={request.surface}
          onClose={closeVerdict}
          onAddPeople={handleAddPeople}
        />
      )}
    </VerdictPromptContext.Provider>
  );
}
